import React, { useEffect, useState } from 'react';
import { Paper, Typography, Tabs, Tab, IconButton, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import ClosedTransactionsGrid from './closedTransactionsGrid';
import CancelledTransactionsGrid from './cancelledTransactionGrid';

const AcceptedQuotes = ({ selectedAgency, refreshToggle, handleViewDetails, canViewRequestingAgency }) => {
  const [acceptedQuotes, setAcceptedQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (selectedAgency) {
      fetchAcceptedRequests();
    }
  }, [selectedAgency, refreshToggle]);

  const fetchAcceptedRequests = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token || !selectedAgency) {
      toast.error('No authentication token or selected agency found.');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}PostingTransferBond/${selectedAgency.id}/AcceptedRequests`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching accepted requests: ${response.statusText}`);
      }

      const data = await response.json();
      // Ensure each row has a unique 'id' property
      setAcceptedQuotes(
        data.map((quote) => ({ ...quote, id: quote.transferBondTransactionID, defendantName: `${quote.defendantFirstName?.trim() || ''} ${quote.defendantLastName?.trim() || ''}`.trim() || 'N/A',
        requestingAgencyName: quote.requestingAgencyName || '-', }))
      );
    } catch (error) {
      toast.error('Error fetching accepted requests');
    }
  };

  const acceptedColumns = [
    {
      field: 'transferBondTransactionID',
      headerName: 'Ref #',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.transferBondTransactionID)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'defendantName',
      headerName: 'Defendant Name',
      width: 250,
      headerAlign: 'center',
      align: 'center',
    },
    ...(canViewRequestingAgency
      ? [
          {
            field: 'requestingAgencyName',
            headerName: 'Requesting Agency',
            width: 200,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                }}
              >
                {params.row.requestingAgencyName || '-'}
              </Box>
            ),
          },
        ]
      : []),
    {
      field: 'county',
      headerName: 'County',
      width: 100,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'state',
      headerName: 'State',
      width: 65,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'totalFees',
      headerName: 'Total Fees',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      valueGetter: (value, row) => `$${calculateTotalFees(row).toFixed(2)}`,
    },
    {
      field: 'Post by',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      width: 325,
      renderCell: (params) => {
        if (params.row.hasBeenPosted === true) {
          return (
            <span style={{ fontStyle: 'italic' }}>
              {'Verification Pending'}
            </span>
          );
        }

        // Get the formatted date and the full date object (acceptDateObj)
        const { formattedDate, acceptDateObj } = addPostingTimeToAcceptDate(
          params.row
        );

        // Compare acceptDateObj with the current date and time
        const now = new Date();
        let isOverdue = false;
        if (acceptDateObj) {
          isOverdue = acceptDateObj < now; // Check if the calculated date is in the past
        }
        return (
          <span>
            {'Post by: ' + formattedDate}{' '}
            {isOverdue && <span style={{ color: 'red' }}>(Overdue)</span>}
          </span>
        );
      },
    },
    {
      field: 'numberOfBonds',
      headerName: '# Bonds',
      width: 100,
      headerAlign: 'center',
      align: 'center',
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const calculateTotalFees = (row) => {
    const executionFee = row?.executionFee || 0;
    const travelFee = row?.travelFee || 0;
    const otherFee = row?.miscFee || 0;
    return executionFee + travelFee + otherFee;
  };

  const addPostingTimeToAcceptDate = (row) => {
    if (row.dateAccepted === null) {
      return { formattedDate: '-', acceptDateObj: null };
    }

    let acceptDate = row.dateAccepted || new Date();
    let postingTime = row.promisedPostingTime || 0; // Time is in minutes
    const acceptDateObj = new Date(acceptDate);

    // Add the posting time (in minutes) to the accepted date
    acceptDateObj.setMinutes(acceptDateObj.getMinutes() + postingTime);

    // Format the date and time in a more compact numeric format
    const formattedDate =
      acceptDateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }) +
      ' ' +
      acceptDateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

    // Return both the formatted date and the full Date object for comparison
    return { formattedDate, acceptDateObj };
  };

  return (
    <Paper sx={{ padding: 3, mb: 4 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Navigate between Accepted Transactions, Closed Transactions, and Cancelled Transactions using the tabs below:
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        aria-label="Quotes Tabs"
        variant="fullWidth"
        sx={{
          '& .Mui-selected': {
            backgroundColor: '#f9f9f9',
            fontWeight: 'bold',
          },
        }}
      >
        <Tab label="Accepted Transactions" />
        <Tab label="Closed Transactions" />
        <Tab label="Cancelled Transactions" />
      </Tabs>

      {activeTab === 0 && (
        <Paper sx={{ mt: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Accepted Transactions
          </Typography>
          <DataGrid
            rows={acceptedQuotes}
            columns={acceptedColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10]}
            pagination
            autoHeight
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-cell': {
                textAlign: 'center',
              },
            }}
          />
        </Paper>
      )}

      {activeTab === 1 && (
        <ClosedTransactionsGrid selectedAgency={selectedAgency} canViewRequestingAgency={canViewRequestingAgency} />
      )}

      {activeTab === 2 && (
        <CancelledTransactionsGrid selectedAgency={selectedAgency} canViewRequestingAgency={canViewRequestingAgency} />
      )}
    </Paper>
  );
};

export default AcceptedQuotes;
